import {useEffect, useRef, useState, useCallback, useMemo} from "react";

import { AgGridReact } from "ag-grid-react";

import { Box, Button, InputAdornment, Menu, MenuItem, TextField } from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import AddIcon from "@mui/icons-material/Add";

import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import StatPill from "@plannel/components/ui/StatPill";
/*
 * 목록 셀·오른쪽 편집 패널·시트 룩은 지표관리(DPMeasureModal)와 **같은 것**을 쓴다.
 * 비슷하게 다시 그리면 반드시 어긋난다 — 실제로 폰트·여백·라벨 위치가 전부 달라 보였다.
 */
import { MeasureListCell } from "@plannel/components/aggrid/MeasureGridCells";
import MeasureDetailPanel from "@plannel/pages/demand-plan/dp-config/MeasureDetailPanel";
import { PANEL } from "@plannel/components/ui/SidePanel";
import "@plannel/scss/components/MeasureSheetGrid.scss";
import StarRateRoundedIcon from "@mui/icons-material/StarRateRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import p13nService from "@plannel/services/system/p13n-service";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import MeasureLayoutModal from "./MeasureLayoutModal";

import { withTranslation } from "react-i18next";
import DataState from "@plannel/components/aggrid/DataState";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import debug from "@plannel/utils/debug-util";
import axiosUtil from "@plannel/utils/axios-bigint";
import dateUtils from "@plannel/utils/date-util";
import codeUtil from "@plannel/pages/utils/code-util";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import TreeMultiSelect from "./HrchyMultiSelect";
import ColorUtil from "@plannel/pages/utils/color-util";

import {size, isEqual, cloneDeep, isUndefined, uniqWith, isEmpty, values, isObject, isArray, includes} from "lodash";
import customerHrchyService from "@plannel/services/data/customer-hrchy-service";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";
import { DPFilterModal } from "./FilterModal";
import MultiSelectLocation from "@plannel/components/modal/MultiSelectLocation";
import dpConfigUtils from "../../dp-config/utils/dp-config-util";
import AbcXyzFilterModal from "@plannel/components/modal/AbcXyzFilterModal";
import { FS } from "@plannel/utils/font-util";
import measureService from "@plannel/services/data/measure-service";

/*
 * 이 파일의 필터 모달 5개(고객 계층 / 품목 계층 / 원산지 / 도착지 / ABC-XYZ)는 셸이 같아 래퍼로 모은다.
 * 확인 버튼은 전부 부모 그리드 행에 값을 써넣고 닫을 뿐 저장하지 않는다 — 실제 저장은
 * 본 모달의 SaveButton 이 한다. 그래서 save 가 아니라 confirm + DoneAll 이다.
 */
const FilterSubModal = ({ t, open, onClose, label, subtitle, onConfirm, size = "md", children }) => (
  <BaseConfigModal
    open={open}
    onClose={onClose}
    title={label}
    subtitle={subtitle}
    onSave={onConfirm}
    saveLabel={t("confirm")}
    saveIcon={<DoneAllIcon />}
    size={size}>
    {children}
  </BaseConfigModal>
);

/*
 * 기본 사용자 정의 표시 — 우측 사이드 패널(LayoutListModal)과 **같은 별**이다.
 * 같은 뜻을 두 화면이 다른 모양(별 / 스위치)으로 말하면 안 된다.
 * 색은 PANEL.star 를 그대로 쓴다 — 별 색이 두 군데로 갈라지지 않게.
 */
const DefaultStarCell = params => {
  if (params.node?.rowPinned) return null;

  const on = !!params.value;
  const enabled = params.enabled !== false;

  return (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%", opacity: enabled ? 1 : 0.45 }}>
      {on
        ? <StarRateRoundedIcon sx={{ fontSize: 18, color: PANEL.star }} />
        : <StarBorderRoundedIcon sx={{ fontSize: 18, color: "text.disabled" }} />}
    </Box>
  );
};

const LayoutManagementModal = (props) => {
  const { t, close, open, pageCd, pageReduxViewState, activeMeasures, displayNames = null } = props;
  const RP_REVIEW = "RP_REVIEW";
  const DP_WORKBENCH = "DP_WORKBENCH";
  const CHANNEL_WORKBENCH = "DP_CHANNEL_WORKBENCH";
  const PSI_SIMULATION = "PSI_SIMULATION";
  const DEFAULT_LAYOUT = "DEFAULT";
  const isDp = isEqual(pageCd, DP_WORKBENCH) || isEqual(pageCd, CHANNEL_WORKBENCH);
  
  const timeDimensions = Object.values(codeUtil.buckets) ?? [];
  const { ADMIN } = codeUtil.roles;
  const { customer, item, itemType: itemTypeMap, filterType } = codeUtil;
  const common = "_COMMON";
  const ITEM_FILTER = "itemFilter";
  const CUST_FILTER = "custFilter";
  const itemTypes = ["FG", "KIT"];
  const currentUser = axiosUtil.jsonbigParse(localStorage.getItem("user"));
  const { snackBarMessageColor : { inputError, saveSuccess, saveFail, noSaveData, warning } } = ColorUtil;

  const gridRef = useRef();
  const [gridApi, setGridApi] = useState(null);
  const [layoutModalData, setLayoutModalData] = useState([]);
  const [custHrchyOpen, setCustHrchyOpen] = useState(false);
  const [prodHrchyOpen, setProdHrchyOpen] = useState(false);
  const [destLocOpen, setDestLocOpen] = useState(false);
  const [originLocOpen, setOriginLocOpen] = useState(false);
  const [measureLayoutOpen, setMeasureLayoutOpen] = useState(false);
  const [invClassOpen, setInvClassOpen] = useState(false);
  const [isRowSelected, setIsRowSelected] = useState(false);
  const [isMeasureClicked, setIsMeasureClicked] = useState(false);
  const [seqCloneRow, setSeqCloneRow] = useState(2);

  const [selectedLayoutData, setSelectedLayoutData] = useState({});
  const [measureLayoutData, setMeasureLayoutData] = useState({});
  const [custHrchyFilter, setCustHrchyFilter] = useState({});
  const [prodHrchyFilter, setProdHrchyFilter] = useState({});
  const [originLocationFilter, setOriginLocationFilter] = useState(null);
  const [destlocationFilter, setDestLocationFilter] = useState(null);
  const [layoutSearch, setLayoutSearch] = useState("");

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [modelCode, setModelCode] = useState(customer.name);

  const [itemHrchyLvConfigs, setItemHrchyLvConfigs] = useState([]);
  const [customerHrchyLvConfigs, setCustomerHrchyLvConfigs] = useState([]);
  const [itemHrchys, setItemHrchys] = useState([]);
  const [customerHrchys, setCustomerHrchys] = useState([]);
  const [custGrpLevel, setCustGrpLevel] = useState([]);
  const [itemGrpLevel, setItemGrpLevel] = useState([]);
  const [grpLevel, setGrpLevel] = useState([]);
  const [invClassCdList, setInvClassCdList] = useState([])
  const [anchorEl, setAnchorEl] = useState(null);
  const addListOpen = Boolean(anchorEl);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [dtos, setDtos] = useState({});
  const [counts, setCounts] = useState({ shown: 0, total: 0, shared: 0 });
  const [channelMeasureOptions, setChannelMeasureOptions] = useState(null);

  const retrieveChannelMeasures = () => {
    measureService.getChannelMeasure()
      .then(response => {
        setChannelMeasureOptions(response.data?.results ?? []);
      })
      .catch(e => {
        debug(e);
      });
  };

  useEffect(() => {
    if (isEqual(pageCd, CHANNEL_WORKBENCH)) {
      retrieveChannelMeasures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAllMeasures = () => {
    if (isEqual(pageCd, RP_REVIEW)) {
      return codeUtil.rpReviewMeasures?.map(mea =>{ return { name: displayNames !== null && displayNames[mea] ? displayNames[mea] : mea, measureCd: mea  }});
    } else if (isEqual(pageCd, PSI_SIMULATION)) {
      return codeUtil.psiSimulationMeasures?.map(mea => { return { name: displayNames !== null && displayNames[mea] ? displayNames[mea] : mea, measureCd: mea }});
    } else if (isEqual(pageCd, CHANNEL_WORKBENCH)) {
      return channelMeasureOptions;
    }
    return null;
  }

  const setGridDatas = (resultList) => {
    const datas = [];
    if (!resultList) {
      setLayoutModalData(datas);
      return;
    }
    const defaultLayout = resultList.filter(data => data.layoutId === DEFAULT_LAYOUT);
    for (const layoutData of resultList) {
      if (layoutData?.layoutId === DEFAULT_LAYOUT) {
        continue;
      }

      if (defaultLayout.length > 0) {
        layoutData.isDefault = isEqual(layoutData.layoutId, defaultLayout[0].layout);
      } else {
        layoutData.isDefault = false;
      }

      if (layoutData.updatedLayout) {
        layoutData.updatedTs = layoutData.updatedLayout;
      }
      if (layoutData.activeMeasures) {
        layoutData.measureSize = size(layoutData.activeMeasures);
      }

      if (isDp) {
        if (layoutData.selectIL?.cd !== item.cd && !isEmpty(layoutData?.invClassCdList)) {
          layoutData.invClassCdList = [];
        }
        if (isEqual(layoutData.selectCL?.cd, customer.cd) && layoutData.customerFilterList !== null) {
          const displayFilterList = [];
          for (const cust of Object.values(layoutData.customerFilterList)) {
            for (const val of cust) {
              displayFilterList.push(val)
            }
          }
          layoutData.custFilter = displayFilterList;
        }

        if (isEqual(layoutData.selectIL?.cd, item.cd) && layoutData.itemFilterList !== null) {
          const displayFilterList = [];
          for (const item of Object.values(layoutData.itemFilterList)) {
            for (const val of item) {
              displayFilterList.push(val)
            }
          }
          layoutData.itemFilter = displayFilterList;
        }
      } else {
        //rp layout 기존과 호환
        if (layoutData?.item) {
          layoutData.itemFilter = [layoutData?.item];
          layoutData.itemFilterList = {itemCd: [layoutData?.item]};
          layoutData.itemFilterType = [filterType.or.cd, filterType.equal.cd];
        } else {
          if (layoutData.itemFilterList !== null) {
            const displayFilterList = [];
            for (const item of Object.values(layoutData.itemFilterList)) {
              for (const val of item) {
                displayFilterList.push(val)
              }
            }
            layoutData.itemFilter = displayFilterList;
          }
        }
      }
      datas.push(layoutData);
    }
    datas.sort((a, b) => {
      if (a.updatedTs < b.updatedTs) {
        return 1;
      } else if (a.updatedTs > b.updatedTs) {
        return -1;
      }
      return 0;
    });

    if (gridApi && !gridApi.destroyCalled) {
      GridUtils.setRowData(gridApi, []);
    }
    setLayoutModalData(datas);
  };

  const handelFilterClose = () => {
    setFilterModalOpen(false);
  };

  const retrieveP13n = () => {
    const p13PageCd = isDp ? pageCd : `${pageCd}_LAYOUT`;
    p13nService.getLayout(p13PageCd)
      .then(response => {
        setDtos(response.data);
        setGridDatas(response.data);
        setLayoutSearch("");
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        DataState.initialize(gridApi);
      })
  };

  const retrieveHrchyGroup = () => {
    const custGrp = [];
    const itemGrp = [];
    hrchyConfigService.getAll()
      .then(response => {
        if (response.data) {
          setGrpLevel(response.data.results)
          for (const result of response.data.results) {
            const row = {
              grpCd: result.grpCd,
              grpName: result.grpName,
              hrchyNum: result.hrchyNum,
              itemHrchyFlg: result?.itemHrchyFlg
            }
            if (result?.itemHrchyFlg) {
              itemGrp.push(row);
            } else {
              custGrp.push(row);
            }
          }
          setCustGrpLevel(uniqWith(custGrp, isEqual))
          setItemGrpLevel(uniqWith(itemGrp, isEqual))
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveHrchyConfigs = () => {
    hrchyConfigService.getAll({ searchFilters: { itemHrchyFlg: true } })
      .then(response => {
        const data = response.data.results;
        setItemHrchyLvConfigs([...data, item]);
      }).catch(e => {
        debug(e);
      });

    hrchyConfigService.getAll({ searchFilters: { itemHrchyFlg: false } })
      .then(response => {
        const data = response.data.results;
        setCustomerHrchyLvConfigs([...data, customer]);
      }).catch(e => {
        debug(e);
      });
  };

  const retrieveHrchies = (hrchyNum = 1) => {
    customerHrchyService.getAll({ searchFilters: { hrchyNum } })
      .then(response => {
        if (response.data) {
          setCustomerHrchys(response.data.results);
        }
      })
      .catch(e => {
        debug(e);
      });

    itemHrchyService.getAll({ searchFilters: { hrchyNum } })
      .then(response => {
        if (response.data) {
          setItemHrchys(response.data.results);
        }
      })
      .catch(e => {
        debug(e);
      })
  }

  const lvValueFormatter = (params) => {
    const value = params?.value ? params.value : params;
    if (value?.seqNum) {
      return value?.name;
    }
    return value?.grpName
  }

  const changeCdtoNm = (value, isItem) => {
    let hrchyNms = value;
    const hrchys = isItem ? itemHrchys : customerHrchys;
    for (const hrchy of hrchys) {
      hrchyNms = hrchyNms.replaceAll(hrchy.hrchyCd, hrchy.name);
    }
    return hrchyNms;
  };

  const itemRenderer = (params) => {
    const value = params?.data ? params.data.itemFilter : [];
    if (value?.length > 0) {
      return value.join(", ");
    }
    return t("notFiltered");
  }

  const hrchyConfigRenderer = (params) => {
    const value = params?.value ? params.value : params;
    const isItem = isEqual(params?.colDef.field, ITEM_FILTER);
    if (value?.length > 0) {
      if (isItem && isEqual(params?.data?.selectIL?.cd, item.cd)) {
        return value.join(", ");
      } else if (!isItem && isEqual(params?.data?.selectCL?.cd, customer.cd)) {
        return value.join(", ");
      } else {
        return changeCdtoNm(value.join(", "), isItem);
      }
    }
    return t("notFiltered");
  }

  const itemHrchyRenderer = (params) => {
    const value = isObject(params) ? params.value : params;
    const isItem = isEqual(params?.colDef.field, "item");
    if (value) {
      if (isItem) {
        return value ?? "";
      }
      if (isArray(value)) {
        if (isEmpty(value)) {
          return t("notFiltered");
        } else {
          return changeCdtoNm(value.join(", "), true)
        }
      } else {
        return changeCdtoNm(value, true);
      }
    }
    return t("notFiltered");
  }

  const locationRenderer = (params) => {
    const filed = params.colDef.field;
    const value = params.value ? typeof params.value === "object" ? params.data[filed] : params.value : null
    if (value === null) {
      return t("notFiltered");
    }
    const name = Array.isArray(value) ? value?.map(location => location?.name) : [value.name];
    if (name?.length > 0) {
      return name.join(", ");
    }
    return t("notFiltered");
  }

  const invClassRenderer = (params) => {
    const filed = params.colDef.field;
    const value = params.value ? typeof params.value === "object" ? params.data[filed] : params.value : null
    if (value === null) {
      return t("notFiltered");
    }
    if (value?.length > 0) {
      return value.join(", ");
    }
    return t("notFiltered");
  }

  /*
   * 닫기 — 저장하지 않은 변경이 있으면 먼저 묻는다.
   *
   * 예전에는 stopEditing 이 셀 편집을 커밋할 틈을 주려고 본문 전체를 setTimeout(100) 안에 넣었고,
   * 그 안에서 예외가 나면 **아무 일도 일어나지 않아 닫기 버튼이 죽은 것처럼 보였다**(실제로 그랬다).
   * 편집이 그리드 셀에서 오른쪽 폼으로 나온 지금은 커밋할 셀이 없으므로 지연시킬 이유가 없다.
   * 그리드 상태 조회도 닫기를 막아설 만한 일이 아니라 실패하면 "변경 없음"으로 보고 닫는다.
   */
  const handleClose = () => {
    try {
      gridApi?.stopEditing(false);
    } catch (e) {
      debug(e);
    }

    let hasUnsaved = false;
    try {
      hasUnsaved = DataState.getAllStateData(gridApi).length > 0;
    } catch (e) {
      debug(e);
    }

    setLayoutSearch("");
    setSnackInfo({ open: false, severity: "", content: "" });

    if (hasUnsaved) {
      setDialogInfo({ open: true, title: "cancel", content: t("MSG_ChangeConfirm") });
    } else {
      close(false);
    }
  };

  /* 예전 헤더 X 가 쓰던 "네 개 다 닫기" 핸들러는 지웠다 — 한 번에 하나만 열리므로
     각 모달의 close(custHrchyClose 등)와 결과가 같고, 이제 취소/배경/X 가 한 핸들러를 쓴다. */

  const onSelectionChanged = (params) => {
    setIsRowSelected(true);
    setIsMeasureClicked(false);
    const selectedRow = params.api.getSelectedRows();

    if (selectedRow.length > 0 && isUndefined(selectedRow[0].cloneRow)) {
      setSelectedLayoutData(selectedRow[0]);
      setSeqCloneRow(2);
    }
  };

  const custHrchyClose = () => {
    setCustHrchyOpen(false);
  }

  const prodHrchyClose = () => {
    setProdHrchyOpen(false);
  }

  const locationClose = () => {
    setDestLocOpen(false);
    setOriginLocOpen(false)
  }
  const invClassListClose = () => {
    setInvClassOpen(false);
  }
  const addListClose = () => {
    setAnchorEl(null);
  };

  const addMenuHandleClick = (event) => {
    setAnchorEl(event.currentTarget);
  }

  const handleMeasureLayoutOpen = () => {
    const selectedNode = gridApi?.getSelectedNodes()[0];
    if (selectedNode) {
      setMeasureLayoutData({
        layoutModalData: selectedNode.data,
        viewName: selectedNode.data.pageCd,
      });
    }

    setMeasureLayoutOpen(true);
    setIsMeasureClicked(false);
  };

  const measureLayoutClose = () => {
    setIsMeasureClicked(false);
    setMeasureLayoutOpen(false);
  }

  const filterGrp = (grp) => {
    const data = grpLevel.filter((el) => el.grpCd === grp.grpCd)
    if (grp?.itemHrchyFlg) {
      setItemHrchyLvConfigs([...data, item]);
    } else {
      setCustomerHrchyLvConfigs([...data, customer]);
    }
    return data[0];
  }

  /*
   * 기본(별)은 **하나뿐이다** — 켠 행만 남기고 나머지는 끈다.
   *   워크벤치를 열 때 자동으로 적용되는 조회 조건이라 둘일 수가 없다.
   *   저장 구조도 그렇다: `DEFAULT` 행이 layoutId 를 **하나만** 담는다(saveP13n 참고).
   *   ⚠ 그래서 화면에서도 여러 개가 켜지게 두면 안 된다 — 저장하면 조용히 하나만 살아남는다.
   *
   * ⚠ `node.setDataValue` 를 쓰지 않는다: 이 컬럼은 일부러 editable:false 라(그래야 체크박스 에디터가
   *   안 뜬다) 그 경로로는 값이 들어가지 않았다. 폼이 쓰는 것과 같은 `node.updateData` 로 통일한다.
   */
  const applyDefault = (api, targetId, next) => {
    if (!api || api.destroyCalled || !targetId) {
      return;
    }

    api.forEachNode(node => {
      const value = isEqual(node?.data?.layoutId, targetId) ? next : false;
      if (!!node?.data?.isDefault === value) {
        return;
      }
      node.updateData({ ...node.data, isDefault: value });
      markUpdated(api, node.id);
    });

    /* 오른쪽 패널이 다른 행을 보고 있어도 그 행의 기본이 꺼졌으면 같이 따라가야 한다 */
    setSelectedLayoutData(prev => {
      if (!prev?.layoutId) return prev;
      const value = isEqual(prev.layoutId, targetId) ? next : false;
      return !!prev.isDefault === value ? prev : { ...prev, isDefault: value };
    });
  };

  const onClikedDefault = params => {
    /* 남의 사용자 정의는 기본으로 지정할 수 없다 — 오른쪽 폼과 같은 규칙을 목록에서도 지킨다 */
    if (!canEdit(params?.data)) {
      setSnackInfo({ open: true, severity: warning, content: t("MSG_CustomReadOnly") });
      return;
    }
    /*
     * ⚠ 상태의 gridApi 가 아니라 **params.api** 를 쓴다.
     *   columnDefs 는 useMemo 라, 그리드가 준비되기(onGridReady) 전에 만들어진 이 핸들러가 그대로 남는다.
     *   그때 잡힌 gridApi 는 아직 null 이라 클릭이 조용히 아무 일도 하지 않았다(실제로 그랬다).
     */
    applyDefault(params?.api, params?.data?.layoutId, !params?.value);
  };


  /* 남의 것은 읽기만 된다 — 그리드의 editable() 과 같은 규칙에 ADMIN 예외를 더한 것(onCellClicked 와 동일). */
  const canEdit = row => !!row?.layoutId
    && (isEqual(row?.owner, currentUser?.username) || includes(currentUser?.roles, ADMIN));

  const readOnly = !canEdit(selectedLayoutData);

  /*
   * 제목 옆 개수 배지. 불러온 배열(layoutModalData)이 아니라 **그리드**에서 센다 —
   * 추가·삭제·공유 토글은 저장 전까지 그리드에만 반영되므로, 배열을 세면 배지가 화면과 어긋난다.
   */
  const refreshCounts = api => {
    if (!api || api.destroyCalled) return;
    let total = 0;
    let shared = 0;
    api.forEachNode(node => {
      total += 1;
      if (node?.data?.shared) shared += 1;
    });
    setCounts({ shown: api.getDisplayedRowCount(), total, shared });
  };

  /*
   * 오른쪽 폼이 값을 쓰는 유일한 통로.
   *
   * 값의 정본은 여전히 ag-grid 다 — 저장(saveP13n)·삭제·추가가 전부 행 상태(DataState)를 보고 움직이므로,
   * 폼에서 고친 값도 그리드 행에 써넣고 같은 방식으로 "고쳐졌다"고 표시해야 한다.
   *
   * ⚠ DataState.setRowState 는 부를 때마다 id 를 배열에 **그냥 push** 한다(중복 검사 없음).
   *   폼은 토글 한 번에도 불리므로 그대로 쓰면 저장 dto 에 같은 행이 여러 번 실린다. 이미 표시된
   *   행이면 건너뛴다. created(새 행)는 updated 로 격하시키지 않는다.
   */
  const markUpdated = (api, rowId) => {
    const states = DataState.getAllStateRow(api);
    if (!states) return;
    const known = [...(states.created ?? []), ...(states.updated ?? [])];
    if (known.some(id => isEqual(String(id), String(rowId)))) return;
    DataState.setRowState(api, rowId, "updated");
  };

  const updateSelected = (patch) => {
    if (!gridApi || gridApi.destroyCalled || !selectedLayoutData?.layoutId) return;
    if (readOnly) {
      setSnackInfo({ open: true, severity: warning, content: t("MSG_CustomReadOnly") });
      return;
    }
    const rowNode = gridApi.getRowNode(selectedLayoutData.layoutId);
    if (!rowNode) return;
    const nextData = { ...rowNode.data, ...patch };
    /*
     * applyTransactionAsync 가 아니라 node.updateData 다 — 비동기 트랜잭션은 다음 플러시까지 셀이
     * 옛 값을 들고 있어서, 폼에서 켠 토글이 왼쪽 목록에 바로 반영되지 않는다(실제로 그렇게 보였다).
     * updateData 는 그 자리에서 값을 넣고 셀을 다시 그린다. cellValueChanged 를 내지 않으므로
     * 아래 markUpdated 와 겹쳐 두 번 표시될 일도 없다.
     */
    rowNode.updateData(nextData);
    markUpdated(gridApi, rowNode.id);
    refreshCounts(gridApi);
    setSelectedLayoutData(nextData);
  };

  /*
   * 레벨 드롭다운의 선택지 — 그리드의 lvConfigValidation 과 같은 규칙이다.
   * 계층 그룹을 고르면 그 그룹의 레벨만 남고, 맨 아래에 최하위(고객/품목 그 자체)를 붙인다.
   */
  const custLevelOptions = useMemo(() => ([
    ...(grpLevel?.filter(config => isEqual(config.grpCd, selectedLayoutData?.selectCHG?.grpCd)) ?? []),
    customer,
  ]), [grpLevel, selectedLayoutData, customer]);

  const itemLevelOptions = useMemo(() => ([
    ...(grpLevel?.filter(config => isEqual(config.grpCd, selectedLayoutData?.selectIHG?.grpCd)) ?? []),
    item,
  ]), [grpLevel, selectedLayoutData, item]);

  /* 계층 그룹이 바뀌면 그 아래 레벨과 필터는 더 이상 유효하지 않다 — 그리드의 onCellValueChanged 와 같은 처리. */
  const changeHrchyGroup = (group, isItem) => {
    const level = filterGrp(group);
    retrieveHrchies(group?.hrchyNum);
    updateSelected(isItem
      ? { selectIHG: group, selectIL: level, itemFilter: null, itemFilterList: null }
      : { selectCHG: group, selectCL: level, custFilter: null, customerFilterList: null });
  };

  const changeLevel = (level, isItem) => {
    updateSelected(isItem
      ? { selectIL: level, itemFilter: null, itemFilterList: null }
      : { selectCL: level, custFilter: null, customerFilterList: null });
  };

  /* 폼에 그대로 쓸 수 있게, 그리드 valueFormatter 들을 행 데이터만 받는 형태로 감싼다.
     (렌더러가 colDef.field 로 고객/품목을 가르므로 그 모양을 그대로 만들어 준다) */
  const showHrchyFilter = field => hrchyConfigRenderer({
    value: selectedLayoutData?.[field],
    colDef: { field },
    data: selectedLayoutData,
  });

  const showLocation = field => locationRenderer({
    value: selectedLayoutData?.[field],
    colDef: { field },
    data: selectedLayoutData,
  });

  const showInvClass = () => invClassRenderer({
    value: selectedLayoutData?.invClassCdList,
    colDef: { field: "invClassCdList" },
    data: selectedLayoutData,
  });

  /* 편집 패널이 값을 올려보내는 통로. 레벨·계층 그룹만 딸린 값(필터)을 함께 비워야 해서 따로 받는다. */
  const handlePanelChange = (key, value) => {
    switch (key) {
      case "selectCHG": return changeHrchyGroup(value, false);
      case "selectIHG": return changeHrchyGroup(value, true);
      case "selectCL": return changeLevel(value, false);
      case "selectIL": return changeLevel(value, true);
      case "isDefault": return applyDefault(gridApi, selectedLayoutData?.layoutId, value);
      default: return updateSelected({ [key]: value });
    }
  };

  const openCustFilter = () => {
    setModelCode(customer.name);
    if (isEqual(selectedLayoutData?.selectCL?.cd, customer.cd)) setFilterModalOpen(true);
    else setCustHrchyOpen(true);
  };

  const openItemFilter = () => {
    setModelCode(item.name);
    if (isEqual(selectedLayoutData?.selectIL?.cd, item.cd)) setFilterModalOpen(true);
    else setProdHrchyOpen(true);
  };

  const selectField = (key, label, options, selectedKey, wide) => ({
    key, label, kind: "select", disabled: readOnly, wide,
    options: options.map(opt => ({ ...opt, selected: isEqual(opt.key, selectedKey) })),
  });

  /*
   * 오른쪽 편집 패널의 칸들. 지표관리와 같은 2열 격자에 얹힌다(라벨은 칸 위, 거터 없음).
   *
   * ⚠ 순서는 **예전 그리드의 컬럼 순서 그대로**다. 오래 쓰던 사람은 눈이 그 차례를 기억하고 있어서,
   *   보기 좋다고 재배열하면 매번 다시 찾아야 한다. 예전 columnDefs 기준:
   *     이름 → (정렬/예외 토글) → 데이터 레벨 5 → 필터 → 지표 → ABC-XYZ → 설명 → (공유·기본 토글)
   *     → 소유자 → 수정일시
   *   불리언 셋(정렬·공유·기본)만 셸 구조상 제목 아래 토글 줄로 모이고, 그 안에서도 원래 차례를 지킨다.
   */
  const buildPanelFields = () => {
    if (!selectedLayoutData?.layoutId) return [];

    const scope = isDp
      ? [
        selectField("selectCHG", t("customerHrchyGroup"),
          custGrpLevel.map(grp => ({ key: grp.grpCd, label: grp.grpName, value: grp })),
          selectedLayoutData?.selectCHG?.grpCd),
        selectField("selectCL", t("customer"),
          custLevelOptions.map(lv => ({ key: lv.cd, label: lvValueFormatter(lv), value: lv })),
          selectedLayoutData?.selectCL?.cd),
        selectField("selectIHG", t("itemHrchyGroup"),
          itemGrpLevel.map(grp => ({ key: grp.grpCd, label: grp.grpName, value: grp })),
          selectedLayoutData?.selectIHG?.grpCd),
        selectField("selectIL", t("item"),
          itemLevelOptions.map(lv => ({ key: lv.cd, label: lvValueFormatter(lv), value: lv })),
          selectedLayoutData?.selectIL?.cd),
        /* 값이 "월/주/일" 한 단어라 한 칸이면 충분하다 — 통칸(wide)으로 두니 혼자 과하게 넓었다 */
        selectField("selectTD", t("time"),
          timeDimensions.map(bucket => ({ key: bucket, label: t(bucket), value: bucket })),
          selectedLayoutData?.selectTD),
      ]
      : [];

    const filters = isDp
      ? [
        { key: "custFilter", label: t("customerHrchyFilter"), kind: "action", disabled: readOnly, value: showHrchyFilter(CUST_FILTER), onClick: openCustFilter },
        { key: "itemFilter", label: t("itemHrchyFilter"), kind: "action", disabled: readOnly, value: showHrchyFilter(ITEM_FILTER), onClick: openItemFilter },
      ]
      : [
        { key: "originLocation", label: t("originLocation"), kind: "action", disabled: readOnly, value: showLocation("originLocation"), onClick: () => setOriginLocOpen(true) },
        { key: "destLocation", label: t("destLocation"), kind: "action", disabled: readOnly, value: showLocation("destLocation"), onClick: () => setDestLocOpen(true) },
        {
          key: "itemHrchy", label: t("itemHrchy"), kind: "action", disabled: readOnly,
          value: itemHrchyRenderer({ value: selectedLayoutData?.itemHrchy, colDef: { field: "itemHrchy" }, data: selectedLayoutData }),
          onClick: () => setProdHrchyOpen(true),
        },
        { key: "item", label: t("item"), kind: "action", disabled: readOnly, value: itemRenderer({ data: selectedLayoutData }), onClick: openItemFilter },
        ...(isEqual(pageCd, RP_REVIEW)
          ? [selectField("itemType", t("itemType"),
            itemTypes.map(type => ({ key: type, label: t(itemTypeMap[type]?.name), value: type })),
            selectedLayoutData?.itemType)]
          : []),
      ];

    return [
      { key: "layout", label: t("layoutName"), kind: "text", value: selectedLayoutData?.layout ?? "", disabled: readOnly },
      ...scope,
      ...filters,
      { key: "activeMeasures", label: t("measure"), kind: "action", disabled: readOnly, value: String(size(selectedLayoutData?.activeMeasures)), onClick: handleMeasureLayoutOpen },
      { key: "invClassCdList", label: t("invClassCd"), kind: "action", disabled: readOnly, value: showInvClass(), onClick: () => setInvClassOpen(true) },
      { key: "descTxt", label: t("descTxt"), kind: "text", value: selectedLayoutData?.descTxt ?? "", disabled: readOnly, wide: true },
      /* 소유자·수정일시는 예전에도 읽기 전용 컬럼이었다 — 고칠 수 없는 칸으로 같은 자리에 남긴다 */
      { key: "owner", label: t("owner"), kind: "text", value: selectedLayoutData?.owner ?? "", disabled: true },
      { key: "updatedTs", label: t("updatedTs"), kind: "text", value: dateUtils.formatDate(selectedLayoutData?.updatedTs, "yyyy-MM-dd"), disabled: true },
    ];
  };

  /* 토글 줄도 예전 컬럼 차례를 따른다: 정렬(또는 예외만) → 공유 → 기본 */
  const panelToggles = () => {
    if (!selectedLayoutData?.layoutId) return [];
    return [
      isDp
        ? { key: "orderByItem", label: t("sortByItem"), checked: !!selectedLayoutData?.orderByItem, disabled: readOnly }
        : { key: "exceptionsOnly", label: t("exceptionsOnly"), checked: !!selectedLayoutData?.exceptionsOnly, disabled: readOnly },
      { key: "shared", label: t("shared"), checked: !!selectedLayoutData?.shared, disabled: readOnly },
      { key: "isDefault", label: t("default"), checked: !!selectedLayoutData?.isDefault, disabled: readOnly },
    ];
  };

  /*
   * 왼쪽 목록의 컬럼. 편집이 오른쪽 폼으로 나가면서 그리드는 "무엇이 있는지 훑고 하나를 고르는" 자리가 됐다
   * — 그래서 컬럼은 식별에 필요한 것만 남기고 전부 읽기 전용이다(지표관리의 왼쪽 시트와 같은 역할).
   * 예외는 기본(isDefault) 하나 — 목록에서 바로 켜고 끄는 게 자연스러워 클릭 토글을 유지한다.
   */
  /*
   * 목록 한 줄에 담을 것 — 지표관리의 measureSummary 와 같은 모양(이름 + 칩 + 오른쪽 설정 요약).
   * 칩은 조회 레벨, 요약은 필터/지표 수처럼 "이 사용자 정의가 무엇을 하는지"다.
   */
  const layoutSummary = data => {
    const scope = isDp
      ? [lvValueFormatter(data?.selectCL), lvValueFormatter(data?.selectIL)]
      : [locationRenderer({ value: data?.originLocation, colDef: { field: "originLocation" }, data })];

    const meta = [
      isDp && data?.selectTD ? t(data.selectTD) : null,
      size(data?.activeMeasures) > 0 ? `${t("measure")} ${size(data.activeMeasures)}` : null,
      data?.owner,
    ].filter(Boolean).join(" · ");

    return {
      name: data?.layout || t("layoutName"),
      fx: false,
      type: scope.filter(Boolean).join(" · "),
      user: !!data?.shared,
      original: null,
      meta,
    };
  };

  /*
   * 목록 컬럼 — 지표관리처럼 **토글 하나 + 요약 한 줄**이다.
   * 나머지 값은 숨긴 채 남긴다: 저장(saveP13n)이 행 데이터를 통째로 읽으므로 컬럼이 없어도 무방하지만,
   * 검색(agSetColumnFilter on layout)이 컬럼을 필요로 한다.
   */
  const columnDefs = useMemo(() => ([
    { headerName: "Seq", field: "seq", hide: true, sort: "asc", suppressColumnsToolPanel: true },
    /*
     * 기본 토글.
     * ⚠ 두 가지를 일부러 하지 않는다.
     *   ① `editable: true` — 값이 boolean 이라 AG Grid 가 셀 데이터 타입을 boolean 으로 추론하고,
     *      편집 상태에 들어간 셀에 **체크박스 에디터**를 띄운다(방금 누른 행만 체크박스로 바뀌어 보였다).
     *      `cellDataType: false` 로 추론까지 끈다.
     *   ② `type: ["booleanColumn"]` — 그 타입이 들고 있는 onCellClicked 가 여기 것을 이겨서
     *      클릭이 아무 일도 하지 않았다(그 핸들러는 editable 인 셀만 토글한다). 타입 없이 직접 그린다.
     */
    {
      headerName: "default", field: "isDefault", width: 76, editable: false, cellDataType: false,
      cellRenderer: DefaultStarCell,
      /* 바꿀 수 있는지(소유자인지)를 직접 알려 준다 — 아니면 흐리게 그린다 */
      cellRendererParams: params => ({ enabled: canEdit(params.data) }),
      onCellClicked: onClikedDefault,
      pinned: "left", lockPinned: true, sortable: false,
    },
    { headerName: "layoutName", field: "layout", hide: true, editable: false, filter: "agSetColumnFilter" },
    {
      colId: "layoutSummary", headerName: "layout", flex: 1, minWidth: 260,
      editable: false, sortable: false,
      cellRenderer: MeasureListCell,
      cellRendererParams: { summary: data => layoutSummary(data), userLabel: t("shared") },
    },
    { headerName: "shared", field: "shared", editable: false, hide: true },
    { headerName: "owner", field: "owner", editable: false, hide: true },
    { headerName: "updatedTs", field: "updatedTs", editable: false, hide: true },
    { headerName: "id", field: "id", editable: false, hide: true },
    { headerName: "userId", field: "userId", editable: false, hide: true },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [gridApi, selectedLayoutData, itemHrchys, customerHrchys]);

  const saveP13n = () => {
    const allStateDatas = DataState.getAllStateData(gridApi);

    if (allStateDatas.length > 0) {
      const today = new ZDate();
      let saveFlg = true;
      const layoutDto = [];
      const sharedLayoutDto = [];

      let defaultLayoutId = null;

      gridApi.forEachNode((node) => {
        const rowData = node.data;
        const rowState = node.rowState;

        if (!defaultLayoutId && rowData?.isDefault) {
          defaultLayoutId = node.data.layoutId ?? null;
        }

        if (!rowData.activeMeasures) {
          saveFlg = false;
          setSnackInfo({ open: true, severity: inputError, content: t("MSG_NoSelectedMeasure") });
        } else if (!rowData.layout) {
          saveFlg = false;
          setSnackInfo({ open: true, severity: inputError, content: t("MSG_CustomNoName") });
        } else {
          const nodeData = {
            layoutId: rowData.layoutId,
            layout: rowData.layout,
            shared: rowData.shared,
            isDefault: rowData.isDefault,
            descTxt: rowData.descTxt,
            owner: rowData.owner,
            userId: rowData.userId,
            rowIndex: rowData.rowIndex,
            activeMeasures: rowData.activeMeasures,
            createdLayout: dateUtils.formatDate(rowData.createdLayout, "yyyy-MM-dd"),
            updatedLayout: dateUtils.formatDate(rowData.updatedLayout, "yyyy-MM-dd"),
          };

          const workbenchKeys = ["orderByItem", "custFilter", "itemFilter", "customerFilterList", "itemFilterList", "custFilterType", "itemFilterType", "invClassCdList",
            "selectCL", "selectIL", "selectTD", "selectCHG", "selectIHG"];
          const rpKeys = ["itemFilterList", "itemFilterType" , "itemHrchy", "itemType", "destLocation", "originLocation", "exceptionsOnly", "invClassCdList"];
          const nodeKeys = isDp ? workbenchKeys : rpKeys;
          for (const key of nodeKeys) {
            nodeData[key] = rowData[key];
          }

          nodeData.createdLayout = dateUtils.formatDate(rowData.createdLayout, "yyyy-MM-dd");
          nodeData.updatedLayout = dateUtils.formatDate(rowData.updatedLayout, "yyyy-MM-dd");

          if (isEqual(rowData?.selectCL?.cd, customer.cd)) {
            delete nodeData.custFilter;
          }

          if (isEqual(rowData?.selectIL?.cd, item.cd)) {
            delete nodeData.itemFilter;
          }

          const dtoComparator = (nodeRowData) => {
            if (nodeRowData.shared) {
              sharedLayoutDto.push(nodeRowData);
            } else {
              layoutDto.push(nodeRowData);
            }
          }

          if (rowState === "created") {
            const createdRow = {
              ...nodeData,
              owner: currentUser.username,
              createdLayout: dateUtils.formatDate(today, "yyyy-MM-dd"),
              updatedLayout: dateUtils.formatDate(today, "yyyy-MM-dd")
            }
            dtoComparator(createdRow);

          } else if (rowState === "updated") {
            const updatedRow = {
              ...nodeData,
              updatedLayout: dateUtils.formatDate(today, "yyyy-MM-dd"),
            }
            dtoComparator(updatedRow);

          } else {
            dtoComparator({
              ...nodeData,
              updatedLayout: rowData.updatedTs,
            })
          }
        }
      });

      if (saveFlg) {
        const p13PageCd = isDp ? pageCd : `${pageCd}_LAYOUT`;
        layoutDto.push({ layoutId: DEFAULT_LAYOUT, layout: defaultLayoutId });

        const p13nDto = {
          pageCd: p13PageCd,
          layoutDtos: layoutDto,
        };

        const p13nSharedDto = {
          pageCd: `${p13PageCd}${common}`,
          layoutDtos: sharedLayoutDto,
        };

        p13nService.upsertLayout([p13nDto, p13nSharedDto])
          .then(() => {
            setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_SaveSuccess") });
            retrieveP13n();
          })
          .catch(e => {
            setSnackInfo({ open: true, severity: saveFail, content: t("MSG_SaveFailed") });
            debug(e);
          });
      }
    } else {
      setSnackInfo({ open: true, severity: noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const deleteP13n = () => {
    const layoutDto = [];
    const sharedLayoutDto = [];

    let deleteFlag = false;
    gridApi?.getSelectedRows().forEach(
      (row) => {

        if (row.owner === currentUser.username || includes(currentUser?.roles, ADMIN)) {
          for (const dto in dtos) {
            if (dtos[dto].layoutId === row.layoutId) {
              deleteFlag = true;
              delete dtos[dto];
            } else {
              if (dtos[dto].shared === true) {
                sharedLayoutDto.push(dtos[dto]);
              } else {
                layoutDto.push(dtos[dto]);
              }
            }
          }
        } else {
          setSnackInfo({ open: true, severity: inputError, content: t("MSG_PermissionDenied") });
        }
      });

    if (deleteFlag) {
      const p13PageCd = isDp ? pageCd : `${pageCd}_LAYOUT`;
      const p13nDto = {
        pageCd: p13PageCd,
        layoutDtos: layoutDto,
      };

      const p13nSharedDto = {
        pageCd: `${p13PageCd}${common}`,
        layoutDtos: sharedLayoutDto,
      };

      p13nService.upsertLayout([p13nDto, p13nSharedDto])
        .then(() => {
          setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveP13n();
        });
    }
  };

  const onGridReady = (params) => {
    DataState.initialize(params.api);
    setGridApi(params.api);
  };

  const onCellValueChanged = (params) => {
    if (params) {
      const colId = params.column?.getId();
      if (isEqual(colId, "selectIL")) {
        params.node.setDataValue(ITEM_FILTER, null);
      } else if (isEqual(colId, "selectCL")) {
        params.node.setDataValue(CUST_FILTER, null);
      }
      DataState.setDataState(params);
    }
  };

  const handleDialog = (action) => {
    gridApi.stopEditing(false);
    if (action === "SAVE") {
      saveP13n();
    } else if (action === "DELETE") {
      deleteP13n();
    } else if (action === "CANCEL") {
      close();
    }
  };

  const addRow = () => {
    GridUtils.focusOnSelectedRow(gridApi);

    const today = new ZDate();
    const endDate = new ZDate(today.getFullYear, today.getMonth + 3, -1);
    const newRow = {
      layoutId: Number(new ZDate()),
      p13nId: null,
      p13n: null,
      pageCd,
      layout: null,
      shared: false,
      isDefault: false,
      userId: null,
      orderByItem: false,
      exceptionsOnly: false,
      descTxt: null,
      owner: currentUser.username,
      rowIndex: 0,
      startDate: dateUtils.formatDate(today, "yyyy-MM"),
      endDate,
      fixedAs: dateUtils.formatDate(today, "yyyy-MM"),
      currentDate: dateUtils.formatDate(today, "yyyy-MM-dd"),
      periodRange: null,
      activeMeasures: null,
      updatedTs: dateUtils.formatDate(today, "yyyy-MM-dd"),
    };
    
     if (isEqual(pageCd, DP_WORKBENCH)) {
      newRow.custFilter = [];
      newRow.customerFilterList = {};
      newRow.selectCL = filterGrp(custGrpLevel[0]);
      newRow.selectCHG = custGrpLevel[0];
      newRow.selectIL = filterGrp(itemGrpLevel[0]);
      newRow.itemFilterList = {};
      newRow.selectIHG = itemGrpLevel[0];
      newRow.selectTD = "month";
      newRow.invClassCdList = [];
    } else if (isEqual(pageCd, PSI_SIMULATION) || isEqual(pageCd, RP_REVIEW)) {
      newRow.originLocation = null;
      newRow.destLocation = null;
      if (isEqual(pageCd, RP_REVIEW)) {
        newRow.itemType = "FG";
      }
      
    }
    DataState.setDataState(GridUtils.addRow(gridApi, newRow));
  };

  const cloneAddRow = () => {
    setSeqCloneRow(seqCloneRow + 1);
    GridUtils.focusOnSelectedRow(gridApi);

    let isSelected = false;

    if (size(selectedLayoutData) < 1) {
      setSnackInfo({ open: true, severity: warning, content: t("MSG_NoSelectedRow") });
    } else {
      isSelected = true;
    }

    if (isSelected) {
      const cloneLayoutData = cloneDeep(selectedLayoutData);
      const today = new ZDate();
      const newCloneRow = {
        ...cloneLayoutData,
        layoutId: Number(new ZDate()),
        userId: null,
        layout: `${cloneLayoutData.layout} (${seqCloneRow})`,
        startDate: dateUtils.formatDate(today, "yyyy-MM"),
        endDate: null,
        currentDate: dateUtils.formatDate(today, "yyyy-MM-dd"),
        owner: currentUser.username,
        rowIndex: cloneLayoutData.rowIndex + 1,
        measureSize: size(cloneLayoutData.activeMeasures),
        updatedTs: dateUtils.formatDate(today, "yyyy-MM-dd"),
        cloneRow: true,
        isDefault: false,
      };
      DataState.setDataState(GridUtils.addRow(gridApi, newCloneRow));
    }
  };

  const getWorkbenchCurrentLayout = () => {
    if (pageReduxViewState) {
      let itemFilter = [];
      let custFilter = [];

      if (!isEmpty(pageReduxViewState?.filter?.category)) {
        pageReduxViewState?.filter?.category.forEach((filter) => itemFilter.push(filter.hrchyCd));
      }
      
      if (!isEmpty(pageReduxViewState?.filter?.customerHrchys)) {
        pageReduxViewState?.filter?.customerHrchys.forEach((filter) => custFilter.push(filter.hrchyCd));
      }
      if (!isEmpty(pageReduxViewState?.itemFilterList)) {
        itemFilter = values(pageReduxViewState.itemFilterList);
      }
      if (!isEmpty(pageReduxViewState?.customerFilterList)) {
        custFilter = values(pageReduxViewState.customerFilterList);
      }
      const planRange = dpConfigUtils.getPlanningRange(pageReduxViewState.startDate, pageReduxViewState.endDate, pageReduxViewState.bucketUnit);
      const today = new ZDate();

      const currentLayout = {
        layoutId: Number(new ZDate()),
        p13nId: null,
        p13n: null,
        pageCd,
        layout: null,
        shared: false,
        isDefault: false,
        orderByItem: pageReduxViewState?.orderByItem,
        descTxt: null,
        owner: currentUser.username,
        rowIndex: 0,
        startDate: pageReduxViewState?.startDate,
        endDate: new ZDate(pageReduxViewState.endDate),
        fixedAs: dateUtils.formatDate(today, "yyyy-MM"),
        currentDate: dateUtils.formatDate(today, "yyyy-MM-dd"),
        periodRange: planRange,
        activeMeasures,
        custFilter,
        itemFilter,
        customerFilterList: pageReduxViewState.customerFilterList,
        itemFilterList: pageReduxViewState.itemFilterList,
        itemFilterType: pageReduxViewState.itemFilterType,
        custFilterType: pageReduxViewState.custFilterType,
        selectCL: customerHrchyLvConfigs.filter((config) => isEqual(config.cd, pageReduxViewState.custLevelCd))[0],
        selectIL: itemHrchyLvConfigs.filter((config) => isEqual(config.cd, pageReduxViewState.itemLevelCd))[0],
        selectCHG: custGrpLevel.filter((config) => isEqual(config.grpCd, pageReduxViewState.custGrpLevelCd))[0],
        selectIHG: itemGrpLevel.filter((config) => isEqual(config.grpCd, pageReduxViewState.itemGrpLevelCd))[0],
        selectTD: pageReduxViewState.timeDimension,
        invClassCdList: pageReduxViewState?.invClassCdList,
        updatedTs: dateUtils.formatDate(today, "yyyy-MM-dd"),
      };
      return currentLayout;
    }
    return null;
  };

  const getRpCurrentLayout = () => {
    if (pageReduxViewState) {
      let itemFilter = [];
      if (!isEmpty(pageReduxViewState?.itemFilterList)) {
        itemFilter = values(pageReduxViewState.itemFilterList);
      }

      let categoryFilter = null;
      if (pageReduxViewState?.filter?.category) {
        if (isArray(pageReduxViewState.filter.category)) {
          categoryFilter = pageReduxViewState.filter.category?.map(itemHrchy => itemHrchy?.hrchyCd);
        } else {
          categoryFilter = [pageReduxViewState.filter.category?.hrchyCd];
        }
      }

      const copyMeasures = cloneDeep(activeMeasures);
      const activeMeasuresMap = copyMeasures.reduce((acc, m, index) => {
        acc[m.measureCd] = { ...m, seqNum: index + 1 };
        return acc;
      }, {});

      const currentLayout = {
        layoutId: Number(new ZDate()),
        p13nId: null,
        p13n: null,
        pageCd,
        layout: null,
        shared: false,
        isDefault: false,
        exceptionsOnly: pageReduxViewState?.onlyExceptionApplied,
        descTxt: null,
        owner: currentUser.username,
        rowIndex: 0,
        originLocation: pageReduxViewState?.filter?.originLocation,
        destLocation: pageReduxViewState?.filter?.destLocation,
        itemType: pageReduxViewState?.itemTypeSearch,
        activeMeasures: activeMeasuresMap,
        itemFilter,
        itemFilterList: pageReduxViewState?.itemFilterList,
        itemFilterType: pageReduxViewState?.itemFilterType,
        itemHrchy: categoryFilter,
        invClassCdList: pageReduxViewState?.invClassCdList,
        updatedTs: dateUtils.formatDate(new ZDate(), "yyyy-MM-dd"),
      }

      return currentLayout;
    }
    return null;
  };

  const addCurrentLayout = () => {
      let currentLayout = null;
      switch (pageCd) {
        case DP_WORKBENCH: {
          currentLayout = getWorkbenchCurrentLayout();
          break;
        }
        case RP_REVIEW: {
          currentLayout = getRpCurrentLayout();
          break;
        }
        case PSI_SIMULATION: {
          currentLayout = getRpCurrentLayout();
          break;
        }
        default: break;
      }
      if (currentLayout) {
        DataState.setDataState(GridUtils.addRow(gridApi, currentLayout));
      }
  }

  const getRowId = useCallback((params) => {
    return params.data?.layoutId ?? "";
  }, []);

  const setFilterValue = (filterHrchyData, filterType, isCust, isBottom) => {
    const selectedRow = gridApi?.getSelectedRows()[0];
    if (size(selectedRow) > 0) {
      let displayFilterList = [];
      const rowNode = gridApi.getRowNode(selectedRow.layoutId);
      if (isBottom) {
        displayFilterList = values(filterHrchyData);
      }
      // hrchy 선택 시 invClassList reset
      let invClassList = selectedRow?.invClassCdList;
      if (selectedRow?.selectIL?.cd !== item.cd && !isEmpty(invClassList)) {
        invClassList = [];
      }

      const hrchyData = isDp ?
      isCust ? { custFilter: isBottom ? displayFilterList : filterHrchyData, customerFilterList: isBottom ? filterHrchyData : null, custFilterType: filterType } 
      : { itemFilter: isBottom ? displayFilterList : filterHrchyData, itemFilterList: isBottom ? filterHrchyData : null, itemFilterType: filterType, invClassCdList: invClassList }
      : { itemHrchy: isBottom ? null : filterHrchyData ?? null, itemFilter: isBottom ? displayFilterList : null, itemFilterList: isBottom ? filterHrchyData : null, itemFilterType: filterType };

      const filteredData = {
        ...rowNode.data,
        ...hrchyData,
      };
      gridApi.applyTransactionAsync({ update: [filteredData] });
      DataState.setRowState(gridApi, rowNode.id, "updated");
      setSelectedLayoutData(filteredData);
    }
  };

  const setLocFilterValue = (originLoc, destLoc) => {
    const selectedRow = gridApi?.getSelectedRows()[0];
    if (size(selectedRow) > 0) {
      const rowNode = gridApi.getRowNode(selectedRow.layoutId);
      const data = {};
      if (originLoc !== null) {
        data.originLocation = originLoc;
      }
      if (destLoc !== null) {
        data.destLocation = destLoc;
      }
      const filteredData = {
        ...rowNode.data,
        ...data,
      };
      gridApi.applyTransactionAsync({ update: [filteredData] });
      DataState.setRowState(gridApi, rowNode.id, "updated");
      setSelectedLayoutData(filteredData);
    }
  };

  const setInvClassValue = (invClassCdList) => {
    const selectedRow = gridApi?.getSelectedRows()[0];
    let isAlert = false;
    //아이템일 때만 confirm 가능하도록
    if (selectedRow?.selectIL?.cd !== item.cd && isDp) {
      setSnackInfo({ open: true, severity: inputError, content: t("MSG_OnlyItem") });
      isAlert = true;
    }
    if (size(selectedRow) > 0) {
      const rowNode = gridApi.getRowNode(selectedRow.layoutId);
      const data = {};
      if (invClassCdList !== null) {
        data.invClassCdList = invClassCdList;
      }
      if (isAlert) {
        data.invClassCdList = [];
      }
      const filteredData = {
        ...rowNode.data,
        ...data,
      };
      gridApi.applyTransactionAsync({ update: [filteredData] });
      DataState.setRowState(gridApi, rowNode.id, "updated");
      setSelectedLayoutData(filteredData);
    }
  };

  useEffect(() => {
    if (open) {
      /* 이 컴포넌트는 닫혀도 언마운트되지 않는다(셸만 숨는다) — 지난번 확인 창이 남아 있으면
         다시 열 때 그대로 뜬다. 열 때마다 고른 행과 확인 창을 비우고 시작한다. */
      setDialogInfo({ open: false, title: "", content: "" });
      setSelectedLayoutData({});
      retrieveHrchyGroup();
      retrieveHrchyConfigs();
      retrieveP13n();

      if (isEqual(pageCd, RP_REVIEW) || isEqual(pageCd, PSI_SIMULATION)) {
        retrieveHrchies();
      }
    } else {
      setSnackInfo({ open: false, severity: "", content: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (gridApi) {
      DataState.initialize(gridApi);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridApi]);

  useEffect(() => {
    if (isMeasureClicked && isRowSelected) {
      handleMeasureLayoutOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRowSelected, isMeasureClicked]);

  useEffect(() => {
    if (gridApi) {
      if (measureLayoutData?.layoutId) {
        const rowNode = gridApi.getRowNode(measureLayoutData.layoutId);

        const filteredData = {
          ...selectedLayoutData, 
          ...measureLayoutData, 
          measureSize: size(measureLayoutData.activeMeasures)
        };
        
        gridApi.applyTransactionAsync({ update: [filteredData] });
        DataState.setRowState(gridApi, rowNode.id, "updated");
        setSelectedLayoutData(filteredData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureLayoutData]);

  const getFilterList = () => {
    if (isDp) {
      return { "Customer": selectedLayoutData?.customerFilterList ?? {},
               "Item": selectedLayoutData?.itemFilterList ?? {} };
    } else {
      return { "Item": selectedLayoutData?.itemFilterList ?? {} };
    } 
  }

  const getFilterType = () => {
    const defaults = [filterType.or.cd, filterType.equal.cd];
    if (isDp) {
      return { "Customer": selectedLayoutData?.custFilterType ?? defaults,
               "Item": selectedLayoutData?.itemFilterType ?? defaults };
    } else {
      return { "Item": selectedLayoutData?.itemFilterType ?? defaults };
    } 
  }

  const onLayoutFilter = (value) => {
    const searchData = [];
    layoutModalData.forEach((data) => {
      if (data.layout?.toLowerCase().includes(value.toLowerCase())) {
        searchData.push(data.layout);
      }
    });
    const instance = gridApi.getFilterInstance("layout");
    instance.setModel({ values: searchData });
    gridApi.onFilterChanged();
  }

  const onLayoutChange = (e) => {
    const value = e.target.value;
    setLayoutSearch(value);
    onLayoutFilter(value);
  };

  const onLayoutSearch = () => {
    onLayoutFilter(layoutSearch);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      onLayoutSearch();
    }
  };

  const handleCustConfirm = () => {
    setFilterValue(custHrchyFilter, null, true, false);
    setCustHrchyOpen(false);
  }

  const handleProdConfirm = () => {

    setFilterValue(prodHrchyFilter, null, false, false);
    setProdHrchyOpen(false);
  }

  const handleOriginLocationConfirm = () => {
    setLocFilterValue(originLocationFilter, null);
    setOriginLocOpen(false);
  }

  const handleDestLocationConfirm = () => {
    setLocFilterValue(null, destlocationFilter);
    setDestLocOpen(false);
  }

  const handleInvClassCdConfirm = () => {
    setInvClassValue(invClassCdList)
    setInvClassOpen(false);
  }

  const setSeletedFilterList = (data) => {
    if (isEqual(data.modelCode, item.name)) {
      setFilterValue(data.filterList, data.filterType, false, true);
    } else {
      setFilterValue(data.filterList, data.filterType, true, true);
    }
  };

  return (
    <>
      {/*
        * 지표관리(DPMeasureModal)와 같은 짜임 — 제목 옆 개수 배지, 검색/추가 툴바,
        * 왼쪽 목록 + 오른쪽 편집 패널, 아래 삭제(부가 액션) + 닫기/저장.
        *
        * 값의 정본은 여전히 ag-grid 다(저장·삭제·추가가 DataState 행 상태로 움직인다).
        * 달라진 것은 **어디서 고치느냐**뿐 — 셀 인라인 편집 대신 오른쪽 폼이 updateSelected() 로 써넣는다.
        */}
      <BaseConfigModal
        open={open}
        onClose={handleClose}
        title={t("dpUserLayouts")}
        titleAdornment={
          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <StatPill component="span" label={t("layout")} value={counts.total} />
            <StatPill component="span" label={t("shared")} value={counts.shared} accent />
          </Box>
        }
        cancelLabel={t("close")}
        onSave={() => {
          gridApi?.stopEditing(false);
          setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") });
        }}
        /* 푸터 좌측은 표시 건수 — 삭제는 고른 행에 대한 동작이라 오른쪽 편집 패널로 옮겼다(지표관리와 같은 자리) */
        extraActions={
          <Box component="span" sx={{ fontSize: FS.BODY, color: "text.disabled" }}>
            {`${counts.shown} / ${counts.total}`}
          </Box>
        }
        width="min(1240px, 94vw)">
        <Box id="layout-management-modal">

          {/* 툴바 — 검색은 왼쪽, 추가는 오른쪽. 밑줄을 긋지 않는다(지표관리와 같은 이유: 본문에 스크롤바가
              생기면 이 선만 짧아져 셸 구분선과 어긋난다). 구분은 아래 그리드의 헤더 행이 해 준다. */}
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, pb: 1.5 }}>
            <TextField
              size="small"
              variant="outlined"
              name="layout"
              value={layoutSearch}
              placeholder={t("search")}
              onKeyPress={handleKeyPress}
              onChange={onLayoutChange}
              sx={{ width: 220, "& .MuiInputBase-input": { fontSize: FS.SUB } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              id="add-button"
              variant="contained"
              disableElevation
              size="small"
              startIcon={<AddIcon />}
              aria-controls={addListOpen ? "add-list-menu" : null}
              aria-haspopup="true"
              aria-expanded={addListOpen ? "true" : "false"}
              onClick={addMenuHandleClick}
              sx={{ height: 30, px: 1.5, borderRadius: 999, fontSize: FS.SUB, textTransform: "none", whiteSpace: "nowrap" }}>
              {t("add")}
            </Button>
          </Box>

          {/* 좌: 목록(고르기) / 우: 편집 — 지표관리와 같은 비율(660 + 나머지)·같은 높이 계산 */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 560px) minmax(0, 1fr)" },
              height: 420,
            }}>
            <Box
              id="layout-management-modal-grid"
              className="ag-theme-balham measure-sheet-grid"
              sx={{ height: "100%", boxSizing: "border-box", px: 1, py: 0.5, borderRight: { md: 1 }, borderColor: { md: "divider" } }}>
              {/* 선택 체크박스 열은 끈다(checkboxes:false) — 고른 행은 행 전체 강조(measure-row-picked)로
                  이미 드러나고, 지표관리도 같은 이유로 꺼 두었다. 선택은 행 클릭으로만 한다. */}
              <AgGridReact
                ref={gridRef}
                rowData={layoutModalData}
                onGridReady={onGridReady}
                columnDefs={GridUtils.getColumnDefs(columnDefs)}
                {...DefaultGridSetting({ title: "dpUserLayouts" })}
                rowHeight={28}
                headerHeight={28}
                rowSelection={{ mode: 'singleRow', enableClickSelection: true, checkboxes: false }}
                onSelectionChanged={onSelectionChanged}
                onModelUpdated={params => refreshCounts(params.api)}
                rowClassRules={{ "measure-row-picked": params => isEqual(params.data?.layoutId, selectedLayoutData?.layoutId) }}
                getRowId={getRowId}
                onCellValueChanged={onCellValueChanged}
              />
            </Box>

            <MeasureDetailPanel
              selected={!!selectedLayoutData?.layoutId}
              instanceKey={selectedLayoutData?.layoutId}
              title={selectedLayoutData?.layout ?? ""}
              placeholderTitle={t("layoutName")}
              empty={t("MSG_NoSelectedRow")}
              badges={selectedLayoutData?.layoutId && layoutSummary(selectedLayoutData).type
                ? [{ label: layoutSummary(selectedLayoutData).type, kind: "chip" }]
                : []}
              toggles={panelToggles()}
              fields={buildPanelFields()}
              onChange={handlePanelChange}
              /* 삭제는 고른 행에 대한 동작이라 패널 안(지표관리와 같은 자리)에 둔다 */
              onDelete={readOnly ? undefined : () => {
                const allStateDatas = DataState.getAllStateData(gridApi);
                setDialogInfo({
                  open: true,
                  title: "delete",
                  content: allStateDatas.length > 0 ? t("MSG_ChangeConfirm") : t("MSG_DeleteConfirm"),
                });
              }}
              deleteLabel={t("delete")}
            />
          </Box>

          <Dialog
            open={dialogInfo.open}
            onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
            actionName={dialogInfo.actionName}
            title={dialogInfo.title}
            content={dialogInfo.content}
            onHandler={handleDialog}
          />
          <Snackbar
            open={snackInfo.open}
            onClose={() => setSnackInfo({ ...snackInfo, open: false })}
            severity={snackInfo.severity}
            content={snackInfo.content}
          />
        </Box>
      </BaseConfigModal>
      {/* md: 제목이 vi 에서 제일 길다 — "Bộ lọc phân cấp khách hàng"(26자)는 20px/700 에서 약 273px 이라 sm(300)이면 접힌다 */}
      <FilterSubModal t={t} open={custHrchyOpen} onClose={custHrchyClose}
        label={t("customerHrchyFilter")} subtitle={t("MSG_SelectItemsToShow")} onConfirm={handleCustConfirm} size="lg">
        {/* 높이 제한 래퍼는 없앴다 — 트리가 자체적으로 maxHeight+스크롤을 갖는데,
            그 위에 헤더(선택 개수/전체선택)가 붙으면서 합이 260 을 넘어 아래가 잘렸다 */}
        <TreeMultiSelect
          open={custHrchyOpen}
          isCust
          isLeaf={isEqual(pageCd, PSI_SIMULATION)}
          level={selectedLayoutData?.selectCL}
          hrchys={selectedLayoutData?.custFilter}
          selectedHrchys={setCustHrchyFilter}
          isIn
        />
      </FilterSubModal>
      <FilterSubModal t={t} open={prodHrchyOpen} onClose={prodHrchyClose}
        label={t("itemHrchyFilter")} subtitle={t("MSG_SelectItemsToShow")} onConfirm={handleProdConfirm} size="lg">
        <TreeMultiSelect
          open={prodHrchyOpen}
          isCust={false}
          isLeaf={isEqual(pageCd, PSI_SIMULATION)}
          level={isEqual(pageCd, "DP_WORKBENCH") ? selectedLayoutData?.selectIL : null}
          hrchys={isDp ? selectedLayoutData?.itemFilter : selectedLayoutData?.itemHrchy ?? []}
          selectedHrchys={setProdHrchyFilter}
          isIn
        />
      </FilterSubModal>
      {/* 로케이션 선택도 토큰 그리드다(사이트가 그룹 제목) — 높이 제한 래퍼는 스크롤을 안쪽에서 받으므로 없앴다 */}
      <FilterSubModal t={t} open={originLocOpen} onClose={locationClose}
        label={t("originLocation")} subtitle={t("MSG_SelectItemsToShow")} onConfirm={handleOriginLocationConfirm}>
        <MultiSelectLocation
          open={originLocOpen}
          filterValue={Array.isArray(selectedLayoutData?.originLocation) ? selectedLayoutData?.originLocation?.map(val => val.locCd) :
            [selectedLayoutData?.originLocation?.locCd]}
          title={"originLocation"}
          selectedLocation={setOriginLocationFilter}
        />
      </FilterSubModal>
      <FilterSubModal t={t} open={destLocOpen} onClose={locationClose}
        label={t("destLocation")} subtitle={t("MSG_SelectItemsToShow")} onConfirm={handleDestLocationConfirm}>
        <MultiSelectLocation
          open={destLocOpen}
          filterValue={Array.isArray(selectedLayoutData?.destLocation) ? selectedLayoutData?.destLocation?.map(val => val.locCd) :
            [selectedLayoutData?.destLocation?.locCd]}
          title={"destLocation"}
          selectedLocation={setDestLocationFilter}
        />
      </FilterSubModal>
      <FilterSubModal t={t} open={invClassOpen} onClose={invClassListClose}
        label={t("invClassCd")} onConfirm={handleInvClassCdConfirm}>
        {/* 토큰 9개는 두 줄에 다 들어간다 — 예전의 maxHeight 360 래퍼는 스크롤을 만들 뿐이라 없앴다 */}
        <AbcXyzFilterModal invClassCdList={selectedLayoutData?.invClassCdList ?? []} setInvClassCdList={setInvClassCdList} isLayout />
      </FilterSubModal>
      <MeasureLayoutModal
        open={measureLayoutOpen}
        close={measureLayoutClose}
        displayModButtons
        data={measureLayoutData}
        allMeasures={getAllMeasures()}
        displayNames={displayNames}
        modalResultData={setMeasureLayoutData}
      />
      <DPFilterModal
        open={filterModalOpen}
        close={handelFilterClose}
        displayModButtons
        modelCode={modelCode}
        modalResultData={setSeletedFilterList}
        filteredList={getFilterList()}
        filterTypes={getFilterType()}
      />
      <Menu
        id="add-list-menu"
        anchorEl={anchorEl}
        open={addListOpen}
        onClose={addListClose}
        aria-labelledby="add-button"
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem sx={{ fontSize: FS.TITLE, p: "3px 5px" }} onClick={addRow}>{t("addEmptyRow")}</MenuItem>
        <MenuItem sx={{ fontSize: FS.TITLE, p: "3px 5px" }} onClick={cloneAddRow}>{t("addreplicatedRow")}</MenuItem>
        <MenuItem sx={{ fontSize: FS.TITLE, p: "3px 5px" }} onClick={addCurrentLayout}>{t("MSG_AddCurrentLayout")}</MenuItem>
      </Menu>
    </>
  )
}

export default withTranslation()(LayoutManagementModal);